import { jwtDecode } from "jwt-decode";
import { useState, createContext, useRef, useEffect, useLayoutEffect } from "react";
import type { ReactNode, MutableRefObject } from "react";
import { updateMenu, cleanupOldUserStorage } from "./U2000AuthUtils";
import { getCleanMenuCopy } from "../../menu/U2000Menu";
import { setGlobalTokenRef } from "../../axios/U2000AxiosInterceptors";

export interface U2000User {
  userName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  organizationId: string | null;
  organizationName?: string | null;
  organizations?: U2000Organizations[] | null;
}

export interface U2000AuthData {
  isAuthenticated: boolean;
  token: string | null;
  menu: any[];
  currentModule: any[];
  currentMenuItem: any | null;
  securityTags: string[];
  user: U2000User;
  isNewLogin?: boolean; // Flag pour distinguer nouveau login vs refresh
}

export type U2000SessionUserPatch = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

export interface U2000AuthContextType {
  auth: U2000AuthData;
  tokenRef: MutableRefObject<string | null>; // Ref partagée pour éviter 401 après refresh/F5
  prelogin: (data: any) => Promise<any>;
  login: (data: any, menu: any) => Promise<any>;
  refresh: (data: any, menu: any) => Promise<any>;
  logout: () => void;
  setCurrentModule: (module: any) => void;
  setCurrentMenuItem: (menuItem: any) => void;
  hasRole: (role: string) => boolean;
  clearNewLoginFlag: () => void;
  markAsNewLogin: () => void;
  /** Met à jour les champs d'affichage session (ex. après édition profil U2106). */
  updateSessionUser: (patch: U2000SessionUserPatch) => void;
}

export interface U2000Organizations {
  uuid: string;
  organizationName: string;
}

export const U2000AuthContext = createContext<U2000AuthContextType | undefined>(undefined);

interface U2000AuthProviderProps {
  children: ReactNode;
}

export const U2000AuthProvider = ({ children }: U2000AuthProviderProps) => {
  const [auth, setAuth] = useState<U2000AuthData>(u2000DefaultAuthData);
  // Ref partagée, toujours à jour : évite 401 après F5/refresh (intercepteurs lisent cette ref)
  const tokenRef = useRef<string | null>(auth.token);

  // useLayoutEffect s'exécute AVANT les useEffect des composants enfants (même cycle de commit)
  // → garantit que tokenRef.current est à jour avant que les requêtes API partent
  useLayoutEffect(() => {
    tokenRef.current = auth.token;
    if (import.meta.env.DEV && auth.token) {
      const tokenPreview = auth.token.substring(0, 20) + "...";
      console.log("🔑 [AUTH] tokenRef.current MIS À JOUR (useLayoutEffect):", tokenPreview);
    }
  }, [auth.token]);

  // Injecter tokenRef global pour les intercepteurs Axios (au montage)
  useEffect(() => {
    setGlobalTokenRef(tokenRef);
    if (import.meta.env.DEV) {
      const tokenPreview = tokenRef.current ? tokenRef.current.substring(0, 20) + "..." : "null";
      console.log("🔧 [AUTH] globalTokenRef injectée au montage, token actuel:", tokenPreview);
    }
  }, []);

  const prelogin = (data: any): Promise<any> => {
    return new Promise((resolve) => {
      setAuth((prevAuth) => {
        const userContextMenu: any[] = [];

        const updatedAuth: U2000AuthData = {
          ...prevAuth,
          isAuthenticated: true,
          token: data.token,
          securityTags: [],
          menu: userContextMenu,
          user: {
            userName: null,
            firstName: null,
            lastName: null,
            email: null,
            organizationId: null,
            organizationName: null,
          },
        };

        resolve({ isAuthenticated: updatedAuth.isAuthenticated, changePasswordOnLogin: data.changePasswordOnLogin, organizations: data.organizations, token: data.token });
        return updatedAuth;
      });
    });
  };

  const login = (data: any, menu: any): Promise<any> => {
    return new Promise((resolve) => {
      setAuth((prevAuth) => {
        const userContext: any = jwtDecode(data.token);
        // Utiliser une copie propre du menu au lieu du menu possiblement muté
        const cleanMenu = menu ? menu : getCleanMenuCopy();
        const extractedOrgId = userContext.organizationUuid || userContext.organizationId || userContext.orgId || null;
        // BL-0339 : filtrer les items `requiresOrganizationId` (ex. U3148) sans org de session
        const userContextMenu: any[] = updateMenu(cleanMenu, userContext.securityTags, extractedOrgId);

        // Nettoyer les données de l'ancien utilisateur si changement d'utilisateur
        // Remarque: ne pas nettoyer lors d'un simple changement d'organisation pour le même utilisateur
        const newUserKey = `${userContext.sub}_${extractedOrgId || "noorg"}`;
        const oldUserKey = prevAuth.user.userName ? `${prevAuth.user.userName}_${prevAuth.user.organizationId || "noorg"}` : null;

        // Nettoyer uniquement si le nom d'utilisateur a changé (vrai changement d'utilisateur)
        if (oldUserKey && prevAuth.user.userName && prevAuth.user.userName !== userContext.sub) {
          cleanupOldUserStorage(newUserKey);
        }

        const updatedAuth: U2000AuthData = {
          ...prevAuth,
          isAuthenticated: true,
          token: data.token,
          securityTags: userContext.securityTags,
          menu: userContextMenu,
          user: {
            userName: userContext.sub,
            firstName: userContext.firstName,
            lastName: userContext.lastName,
            email: userContext.email,
            organizationId: extractedOrgId,
            organizationName: userContext.organizationName || null,
            organizations: data.organizations || null,
          },
        };

        resolve({ isAuthenticated: updatedAuth.isAuthenticated, changePasswordOnLogin: data.changePasswordOnLogin, organizations: data.organizations });
        return updatedAuth;
      });
    });
  };

  const refresh = (data: any, menu: any): Promise<any> => {
    return new Promise((resolve) => {
      setAuth((prevAuth) => {
        const userContext: any = jwtDecode(data.token);
        // Utiliser une copie propre du menu au lieu du menu possiblement muté
        const cleanMenu = menu ? menu : getCleanMenuCopy();
        const extractedOrgId = userContext.organizationUuid || userContext.organizationId || userContext.orgId || null;
        // BL-0339 : filtrer les items `requiresOrganizationId` (ex. U3148) sans org de session
        const userContextMenu: any[] = updateMenu(cleanMenu, userContext.securityTags, extractedOrgId);

        // Nettoyer les données de l'ancien utilisateur si changement d'utilisateur
        // Remarque: ne pas nettoyer lors d'un simple changement d'organisation pour le même utilisateur
        const newUserKey = `${userContext.sub}_${extractedOrgId || "noorg"}`;
        const oldUserKey = prevAuth.user.userName ? `${prevAuth.user.userName}_${prevAuth.user.organizationId || "noorg"}` : null;

        // Nettoyer uniquement si le nom d'utilisateur a changé (vrai changement d'utilisateur)
        if (oldUserKey && prevAuth.user.userName && prevAuth.user.userName !== userContext.sub) {
          cleanupOldUserStorage(newUserKey);
        }

        const updatedAuth: U2000AuthData = {
          ...prevAuth,
          isAuthenticated: true,
          token: data.token,
          securityTags: userContext.securityTags,
          menu: userContextMenu,
          isNewLogin: false, // Flag pour indiquer un refresh
          user: {
            userName: userContext.sub,
            firstName: userContext.firstName,
            lastName: userContext.lastName,
            email: userContext.email,
            organizationId: extractedOrgId,
            organizationName: userContext.organizationName || null,
            organizations: data.organizations || null,
          },
        };

        console.log("🔄 [AUTH] Refresh token:", {
          userName: userContext.sub,
          isNewLogin: false,
          organizationId: extractedOrgId,
        });
        console.log("🔐 [AUTH] Session restaurée (refresh):", {
          userName: userContext.sub,
          isNewLogin: false,
          organizationId: extractedOrgId,
        });
        resolve(updatedAuth);
        return updatedAuth;
      });
    });
  };

  const logout = (): void => {
    // Pas de nettoyage du localStorage au logout simple
    // L'utilisateur doit retrouver ses préférences à sa prochaine connexion

    setAuth(u2000DefaultAuthData);
  };

  const setCurrentModule = (module: any): void => {
    setAuth((prevAuth) => ({
      ...prevAuth,
      currentModule: module,
    }));
  };

  const setCurrentMenuItem = (menuItem: any): void => {
    setAuth((prevAuth) => ({
      ...prevAuth,
      currentMenuItem: menuItem,
    }));
  };

  const hasRole = (role: string): boolean => {
    return auth.securityTags.some((item) => item === role);
  };

  const clearNewLoginFlag = (): void => {
    console.log("🧹 [AUTH] clearNewLoginFlag called");
    setAuth((prevAuth) => ({
      ...prevAuth,
      isNewLogin: false,
    }));
  };

  const markAsNewLogin = (): void => {
    console.log("🔥 [AUTH] markAsNewLogin called - forcing isNewLogin to true");
    setAuth((prevAuth) => ({
      ...prevAuth,
      isNewLogin: true,
    }));
  };

  const updateSessionUser = (patch: U2000SessionUserPatch): void => {
    setAuth((prevAuth) => ({
      ...prevAuth,
      user: {
        ...prevAuth.user,
        ...(patch.firstName !== undefined ? { firstName: patch.firstName } : {}),
        ...(patch.lastName !== undefined ? { lastName: patch.lastName } : {}),
        ...(patch.email !== undefined ? { email: patch.email } : {}),
      },
    }));
  };

  const contextValue: U2000AuthContextType = {
    auth,
    tokenRef,
    prelogin,
    login,
    refresh,
    logout,
    setCurrentModule,
    setCurrentMenuItem,
    hasRole,
    clearNewLoginFlag,
    markAsNewLogin,
    updateSessionUser,
  };

  return <U2000AuthContext.Provider value={contextValue}>{children}</U2000AuthContext.Provider>;
};

const u2000DefaultAuthData: U2000AuthData = {
  isAuthenticated: false,
  token: null,
  menu: [],
  currentModule: [],
  currentMenuItem: null,
  securityTags: [],
  isNewLogin: false,
  user: {
    userName: "",
    firstName: "",
    lastName: "",
    email: "",
    organizationId: "",
    organizationName: "",
    organizations: null,
  },
};
